
import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGameLoader } from '@/hooks/useGameLoader';
import GameHeader from '@/components/GameHeader';
import GameFrame from '@/components/GameFrame';
import GameFooter from '@/components/GameFooter';
import GameNotFound from '@/components/GameNotFound';
import GameCompletion from '@/components/GameCompletion';
import SEOHead from '@/components/SEOHead';
import { Skeleton } from '@/components/ui/skeleton';

const GamePage = () => {
  const { gameSlug } = useParams(); // Changed from gameId to gameSlug
  const navigate = useNavigate();
  const { game, loading, sessionId, startTime } = useGameLoader(gameSlug); // Pass the slug instead of ID
  
  // Unified back click handler that directly uses navigate
  const handleBackClick = () => {
    // Directly navigate to the home page without going through any middlewares
    navigate('/', { replace: true });
  };

  // Add effect to ensure we're scrolled to the top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-cream-light flex flex-col">
        <div className="h-16 px-4 flex items-center border-b bg-white">
          <Skeleton className="h-8 w-32" />
        </div>
        <div className="flex-grow flex items-center justify-center p-4">
          <div className="w-full max-w-3xl aspect-video">
            <Skeleton className="w-full h-full" />
          </div>
        </div>
        <div className="p-4 border-t bg-white">
          <Skeleton className="h-4 w-24 mb-2" />
          <Skeleton className="h-4 w-64" />
        </div>
      </div>
    );
  }

  if (!game) {
    return <GameNotFound onBackClick={handleBackClick} />;
  }

  return (
    <div className="min-h-screen bg-cream-light flex flex-col">
      {/* SEO Head with dynamic game title */}
      <SEOHead 
        title={game.title} 
        description={`Play ${game.title} - ${game.description}`} 
      />
      
      {/* Header */}
      <GameHeader title={game.title} onBackClick={handleBackClick} />

      {/* Game Container */}
      <div className="flex-grow flex items-center justify-center p-4">
        <GameFrame title={game.title} url={game.url} isCustom={game.isCustom} />
      </div>

      {/* Footer */}
      <GameFooter duration={game.duration} description={game.description} />

      {/* Game Completion Logic */}
      <GameCompletion 
        sessionId={sessionId} 
        startTime={startTime}
        onBackClick={handleBackClick}
      />
    </div>
  );
};

export default GamePage;
